import { SEO } from "@/components/seo/SEO";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Shield, FileText, AlertTriangle } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { LanguageSwitcher } from "@/components/shared/LanguageSwitcher";

const PrivacyPolicyEN = () => {
  const navigate = useNavigate();
  const lastUpdated = "December 20, 2024";
  const companyName = "EasyFinder";
  const websiteUrl = "https://easyfinder.fr";
  const contactEmail = "contact@easyfinder.fr";
  const dpoEmail = "dpo@easyfinder.fr";

  return (
    <>
      <SEO
        title="Privacy Policy | EasyFinder"
        description="EasyFinder Privacy Policy. Learn how we collect, use, and protect your personal data."
        canonical="/privacy-policy-en"
      />
      <div className="min-h-screen bg-background">
        <div className="container max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <LanguageSwitcher frPath="/privacy-policy" enPath="/privacy-policy-en" />
          </div>

          <article className="prose prose-slate dark:prose-invert max-w-none">
            <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
            <p className="text-muted-foreground mb-8">
              Last updated: {lastUpdated}
            </p>

            {/* Introduction */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
              <p>
                {companyName} ("we", "our", "us") is committed to protecting the privacy of its users ("you", "your"). 
                This Privacy Policy explains how we collect, use, store, and protect your personal data 
                when you use our document management application accessible at {websiteUrl} (the "Service").
              </p>
              <p>
                By using our Service, you agree to the practices described in this Privacy Policy. 
                If you do not agree with these practices, please do not use our Service.
              </p>
            </section>

            {/* Data Controller */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">2. Data Controller</h2>
              <p>The data controller for personal data is:</p>
              <ul>
                <li><strong>Company:</strong> {companyName}</li>
                <li><strong>Website:</strong> {websiteUrl}</li>
                <li><strong>Contact email:</strong> {contactEmail}</li>
                <li><strong>Data Protection Officer (DPO):</strong> {dpoEmail}</li>
              </ul>
            </section>

            {/* Data Collected */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">3. Personal Data Collected</h2>
              
              <h3 className="text-xl font-medium mt-6 mb-3">3.1 Data you provide directly</h3>
              <ul>
                <li><strong>Account information:</strong> Email address, full name, profile picture (optional)</li>
                <li><strong>Documents:</strong> Files you upload (PDF, images, Office documents)</li>
                <li><strong>Communications:</strong> Messages you send us via support</li>
              </ul>

              <h3 className="text-xl font-medium mt-6 mb-3">3.2 Automatically collected data</h3>
              <ul>
                <li><strong>Usage data:</strong> Pages visited, features used, timestamps</li>
                <li><strong>Technical data:</strong> Browser type, operating system, IP address (anonymized in logs)</li>
                <li><strong>Session data:</strong> Authentication tokens (encrypted with AES-256-GCM)</li>
              </ul>

              <h3 className="text-xl font-medium mt-6 mb-3">3.3 Data from third-party services</h3>
              
              <h4 className="text-lg font-medium mt-4 mb-2">Google (Gmail and Google Sign-in)</h4>
              <p>If you choose to connect your Gmail account, we access:</p>
              <ul>
                <li>Your Google email address (for authentication)</li>
                <li>Email attachments (only if you enable Gmail import)</li>
                <li>Email metadata (sender, date, subject - to filter relevant attachments)</li>
              </ul>
              
              <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4 my-4">
                <h5 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">
                  🔒 Google API Limited Use Disclosure
                </h5>
                <p className="text-blue-700 dark:text-blue-300 text-sm">
                  EasyFinder's use and transfer to any other app of information received from Google APIs 
                  will adhere to the <a 
                    href="https://developers.google.com/terms/api-services-user-data-policy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="underline font-medium"
                  >
                    Google API Services User Data Policy
                  </a>, including the Limited Use requirements.
                </p>
                <p className="text-blue-700 dark:text-blue-300 text-sm mt-2">
                  We use Gmail data <strong>only</strong> to:
                </p>
                <ul className="text-blue-700 dark:text-blue-300 text-sm mt-1 ml-4">
                  <li>Import email attachments into your EasyFinder workspace</li>
                  <li>Extract text from documents to enable search functionality</li>
                </ul>
                <p className="text-blue-700 dark:text-blue-300 text-sm mt-2">
                  We <strong>never</strong> share your Gmail data with third parties, do not use it for advertising purposes, 
                  and do not retain it beyond what is necessary to provide the Service.
                </p>
              </div>

              <h4 className="text-lg font-medium mt-4 mb-2">Dropbox</h4>
              <p>If you connect Dropbox, we access the files you select for import.</p>

              <h4 className="text-lg font-medium mt-4 mb-2">Stripe (Payments)</h4>
              <p>
                For payments, we use Stripe which collects your payment information directly. 
                We <strong>never</strong> store your credit card numbers. 
                We only receive from Stripe: customer ID, subscription status, payment history.
              </p>
            </section>

            {/* Purpose of Processing */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">4. Purpose of Processing</h2>
              <p>We use your personal data for:</p>
              
              <table className="w-full border-collapse border border-border mt-4">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border p-3 text-left">Purpose</th>
                    <th className="border border-border p-3 text-left">Legal Basis (GDPR)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border p-3">Provide and maintain the Service</td>
                    <td className="border border-border p-3">Contract performance (Art. 6.1.b)</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Manage your user account</td>
                    <td className="border border-border p-3">Contract performance (Art. 6.1.b)</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Process your payments</td>
                    <td className="border border-border p-3">Contract performance (Art. 6.1.b)</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Extract and index document content</td>
                    <td className="border border-border p-3">Contract performance (Art. 6.1.b)</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Send notifications (expiration, shares)</td>
                    <td className="border border-border p-3">Legitimate interest (Art. 6.1.f)</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Improve the Service and fix bugs</td>
                    <td className="border border-border p-3">Legitimate interest (Art. 6.1.f)</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Comply with legal obligations</td>
                    <td className="border border-border p-3">Legal obligation (Art. 6.1.c)</td>
                  </tr>
                </tbody>
              </table>
            </section>

            {/* Data Security */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">5. Data Security</h2>
              <p>We implement technical and organizational security measures to protect your data:</p>
              <ul>
                <li><strong>Encryption in transit:</strong> All communications use HTTPS/TLS 1.3</li>
                <li><strong>Encryption at rest:</strong> OAuth tokens are encrypted with AES-256-GCM</li>
                <li><strong>Access control:</strong> RLS (Row Level Security) policies on all database tables</li>
                <li><strong>Secure authentication:</strong> OAuth 2.0 with PKCE, HMAC-SHA256 signed tokens</li>
                <li><strong>Log anonymization:</strong> Emails and IDs are anonymized in server logs</li>
                <li><strong>CSRF protection:</strong> Signed state parameters for OAuth flows</li>
                <li><strong>Security headers:</strong> HSTS, X-Content-Type-Options, Referrer-Policy</li>
              </ul>
            </section>

            {/* Data Retention */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">6. Data Retention</h2>
              <table className="w-full border-collapse border border-border mt-4">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border p-3 text-left">Data Type</th>
                    <th className="border border-border p-3 text-left">Retention Period</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border p-3">Account data</td>
                    <td className="border border-border p-3">Until account deletion</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Uploaded documents</td>
                    <td className="border border-border p-3">Until deletion by user or account deletion</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">OAuth tokens (Gmail, Dropbox)</td>
                    <td className="border border-border p-3">Until service disconnection or account deletion</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Audit logs</td>
                    <td className="border border-border p-3">12 months</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Payment data (via Stripe)</td>
                    <td className="border border-border p-3">Per Stripe's policy + legal requirements (10 years)</td>
                  </tr>
                </tbody>
              </table>
            </section>

            {/* Data Sharing */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">7. Data Sharing</h2>
              <p>We <strong>never</strong> sell your personal data. We only share your data with:</p>
              
              <h3 className="text-xl font-medium mt-6 mb-3">7.1 Sub-processors</h3>
              <table className="w-full border-collapse border border-border mt-4">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border p-3 text-left">Sub-processor</th>
                    <th className="border border-border p-3 text-left">Purpose</th>
                    <th className="border border-border p-3 text-left">Location</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border p-3">Supabase</td>
                    <td className="border border-border p-3">Hosting, database, authentication</td>
                    <td className="border border-border p-3">EU (AWS eu-central-1)</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Cloudflare</td>
                    <td className="border border-border p-3">CDN, DDoS protection, DNS</td>
                    <td className="border border-border p-3">Global (with EU options)</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Stripe</td>
                    <td className="border border-border p-3">Payment processing</td>
                    <td className="border border-border p-3">EU/US (PCI-DSS certified)</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">OpenAI</td>
                    <td className="border border-border p-3">Document classification and extraction (optional)</td>
                    <td className="border border-border p-3">US (DPA agreement)</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Resend</td>
                    <td className="border border-border p-3">Transactional emails</td>
                    <td className="border border-border p-3">US</td>
                  </tr>
                </tbody>
              </table>

              <h3 className="text-xl font-medium mt-6 mb-3">7.2 Other sharing cases</h3>
              <ul>
                <li><strong>With your consent:</strong> When you share a document via a share link</li>
                <li><strong>Legal obligations:</strong> If required by law or court order</li>
                <li><strong>Rights protection:</strong> To protect our rights, security, or that of others</li>
              </ul>
            </section>

            {/* International Transfers */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">8. International Transfers</h2>
              <p>
                Some of our sub-processors are located outside the European Economic Area (EEA). 
                In these cases, we ensure appropriate safeguards are in place:
              </p>
              <ul>
                <li>Standard Contractual Clauses (SCCs) approved by the European Commission</li>
                <li>EU-US Data Privacy Framework (DPF) for transfers to the United States</li>
                <li>Additional security certifications and measures</li>
              </ul>
            </section>

            {/* Your Rights */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">9. Your Rights (GDPR)</h2>
              <p>Under the General Data Protection Regulation (GDPR), you have the following rights:</p>
              
              <ul className="mt-4">
                <li>
                  <strong>Right of access (Art. 15):</strong> Obtain a copy of your personal data
                </li>
                <li>
                  <strong>Right to rectification (Art. 16):</strong> Correct inaccurate or incomplete data
                </li>
                <li>
                  <strong>Right to erasure (Art. 17):</strong> Request deletion of your data 
                  (available via Settings → Delete my account)
                </li>
                <li>
                  <strong>Right to restriction (Art. 18):</strong> Limit processing of your data in certain cases
                </li>
                <li>
                  <strong>Right to data portability (Art. 20):</strong> Receive your data in a structured, machine-readable format 
                  (available via Settings → Export my data)
                </li>
                <li>
                  <strong>Right to object (Art. 21):</strong> Object to processing based on legitimate interest
                </li>
                <li>
                  <strong>Right to withdraw consent:</strong> At any time for consent-based processing
                </li>
              </ul>

              <p className="mt-4">
                To exercise your rights, contact us at <a href={`mailto:${dpoEmail}`} className="text-primary underline">{dpoEmail}</a>.
              </p>

              <p className="mt-4">
                You also have the right to lodge a complaint with a supervisory authority. 
                In France, this is the CNIL: <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-primary underline">www.cnil.fr</a>
              </p>
            </section>

            {/* Cookies */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">10. Cookies</h2>
              <p>EasyFinder uses only essential cookies necessary for the Service to function:</p>
              <ul>
                <li><strong>Authentication cookies:</strong> To maintain your session</li>
                <li><strong>Preference cookies:</strong> To remember your settings (theme, language)</li>
              </ul>
              <p className="mt-4">
                We do not use advertising cookies or third-party tracking cookies. 
                You can manage cookies via your browser settings.
              </p>
            </section>

            {/* Changes */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">11. Changes to this Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. 
                We will notify you of any material changes by:
              </p>
              <ul>
                <li>Posting the new policy on this page with an updated date</li>
                <li>Sending an email notification for significant changes</li>
              </ul>
              <p className="mt-4">
                We encourage you to review this page periodically to stay informed about our privacy practices.
              </p>
            </section>

            {/* Contact */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">12. Contact Us</h2>
              <p>For any questions about this Privacy Policy or our privacy practices, contact us at:</p>
              <ul>
                <li><strong>General contact:</strong> <a href={`mailto:${contactEmail}`} className="text-primary underline">{contactEmail}</a></li>
                <li><strong>Data Protection Officer:</strong> <a href={`mailto:${dpoEmail}`} className="text-primary underline">{dpoEmail}</a></li>
              </ul>
            </section>

            {/* Google API Disclosure */}
            <section className="mb-8 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-6">
              <h2 className="text-2xl font-semibold mb-4 text-amber-800 dark:text-amber-200">
                13. Google API Limited Use Disclosure
              </h2>
              <p className="text-amber-700 dark:text-amber-300">
                This section details our compliance with the Google API Services User Data Policy.
              </p>
              
              <h3 className="text-lg font-medium mt-4 mb-2 text-amber-800 dark:text-amber-200">Gmail Scopes Used</h3>
              <ul className="text-amber-700 dark:text-amber-300">
                <li><code>https://www.googleapis.com/auth/gmail.readonly</code> - Read emails and attachments</li>
                <li><code>https://www.googleapis.com/auth/userinfo.email</code> - Access your email address</li>
                <li><code>https://www.googleapis.com/auth/userinfo.profile</code> - Access your basic profile information</li>
              </ul>

              <h3 className="text-lg font-medium mt-4 mb-2 text-amber-800 dark:text-amber-200">Limited Use</h3>
              <p className="text-amber-700 dark:text-amber-300">
                In compliance with Google's Limited Use requirements:
              </p>
              <ul className="text-amber-700 dark:text-amber-300">
                <li>✅ We use Gmail data <strong>only</strong> to provide and improve document import features</li>
                <li>✅ We do <strong>not</strong> transfer Gmail data to third parties except as necessary to provide the Service</li>
                <li>✅ We do <strong>not</strong> use Gmail data for advertising purposes</li>
                <li>✅ We allow complete deletion of Gmail data via account settings</li>
                <li>✅ OAuth tokens are encrypted and stored securely</li>
              </ul>

              <h3 className="text-lg font-medium mt-4 mb-2 text-amber-800 dark:text-amber-200">Revoking Access</h3>
              <p className="text-amber-700 dark:text-amber-300">
                You can revoke EasyFinder's access to your Gmail data at any time:
              </p>
              <ol className="text-amber-700 dark:text-amber-300">
                <li>In EasyFinder: Settings → Sources → Gmail → Disconnect</li>
                <li>Via Google: <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer" className="underline">myaccount.google.com/permissions</a></li>
              </ol>
            </section>

            {/* Related Security Documents */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">14. Related Security Documents</h2>
              <p>
                For complete transparency about our security practices, please also see the following documents:
              </p>
              
              <div className="grid gap-4 mt-6 not-prose">
                <Link 
                  to="/information-security-policy-en" 
                  className="flex items-start gap-4 p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                >
                  <div className="p-2 rounded-full bg-primary/10">
                    <Shield className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Information Security Policy</h3>
                    <p className="text-sm text-muted-foreground">
                      Password Policy, Disaster Recovery, security governance
                    </p>
                  </div>
                </Link>

                <Link 
                  to="/incident-response-policy-en" 
                  className="flex items-start gap-4 p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                >
                  <div className="p-2 rounded-full bg-destructive/10">
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Incident Response Policy</h3>
                    <p className="text-sm text-muted-foreground">
                      Detection, classification, and response procedures for security incidents
                    </p>
                  </div>
                </Link>

                <Link 
                  to="/vulnerability-disclosure-policy-en" 
                  className="flex items-start gap-4 p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                >
                  <div className="p-2 rounded-full bg-amber-500/10">
                    <FileText className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Vulnerability Disclosure Policy</h3>
                    <p className="text-sm text-muted-foreground">
                      Responsible vulnerability reporting program
                    </p>
                  </div>
                </Link>
              </div>
            </section>

          </article>

          <div className="mt-8 pt-8 border-t text-center text-sm text-muted-foreground">
            <p>© {new Date().getFullYear()} {companyName}. All rights reserved.</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicyEN;
